import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import DutchFlag from './DutchFlag';
import EnglishFlag from './EnglishFlag';

const LanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Normalize language code to just 'nl' or 'en'
  const normalizedLang = language.substring(0, 2).toLowerCase();
  
  // When component mounts, ensure default language is Dutch if not set
  useEffect(() => {
    if (normalizedLang !== 'nl' && normalizedLang !== 'en') {
      changeLanguage('nl');
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle scroll event to change color of arrow icon and language label
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Add changeLanguage and normalizedLang to dependencies
    if (normalizedLang) {
      changeLanguage(normalizedLang);
    }
  }, [changeLanguage, normalizedLang]); // Add missing dependencies

  const toggleDropdown = () => setIsOpen(!isOpen);
  
  const selectLanguage = (lng) => {
    changeLanguage(lng);
    setIsOpen(false);
  };

  return (
    <div className="relative z-50" ref={dropdownRef}>
      <button 
        onClick={toggleDropdown}
        className="flex items-center focus:outline-none"
        aria-label="Change language"
      >
        {normalizedLang === 'nl' ? (
          <DutchFlag className="w-6 h-6 rounded-sm shadow-sm" />
        ) : (
          <EnglishFlag className="w-6 h-6 rounded-sm shadow-sm" />
        )}
        <span className={`ml-1 text-xs ${isScrolled ? 'text-black' : 'text-white'}`}>
          {normalizedLang === 'nl' ? 'NL' : 'EN'}
        </span>
        <svg
          className={`w-4 h-4 ml-1 transition-transform duration-300 ${
            isOpen ? 'transform rotate-180' : ''
          } ${isScrolled ? 'text-black' : 'text-white'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 py-2 w-24 bg-white rounded-lg shadow-xl">
          <button
            onClick={() => selectLanguage('nl')}
            className={`flex items-center w-full px-3 py-2 text-sm ${
              normalizedLang === 'nl' ? 'bg-gray-100' : ''
            } hover:bg-gray-100`}
          >
            <DutchFlag className="w-5 h-5 mr-2 rounded-sm" />
            <span>NL</span>
          </button>
          <button
            onClick={() => selectLanguage('en')}
            className={`flex items-center w-full px-3 py-2 text-sm ${
              normalizedLang === 'en' ? 'bg-gray-100' : ''
            } hover:bg-gray-100`}
          >
            <EnglishFlag className="w-5 h-5 mr-2 rounded-sm" />
            <span>EN</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
